import { notify } from '@/lib/toast';
import { openPrintWindow } from '@/lib/print-document';
import { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSocketEvent } from '@/hooks/useSocketEvent';
import { Truck, Plus, Eye, PackageSearch, Package, Trash2, CheckCircle2, Printer, ScanBarcode } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { SearchInput } from '@/components/ui/search-input';
import { StatusBadge } from '@/components/ui/status-badge';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { PageHeader } from '@/components/ui/page-header';
import { Dialog } from 'primereact/dialog';
import { Textarea } from '@/components/ui/textarea';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { FormDialog } from '@/components/ui/form-dialog';
import { TableRowActions } from '@/components/ui/table-row-actions';
import api from '@/lib/api';
import { isApiError } from '@/lib/api-error';
import { formatCurrency, formatDate } from '@/lib/utils';
import { StatCard } from '@/components/ui/stat-card';
import { Combobox } from '@/components/ui/combobox';
import { posService } from '@/services/pos.service';
import { useAuthStore } from '@/stores/auth.store';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Dropdown } from 'primereact/dropdown';
import { pendingQty as linePendingQty, receiveHeading, receiveQtyFor } from './receive-qty';


type PurchaseFormItem = {
  productId: string;
  quantity: number;
  unitCost: number;
  taxRate: number;
};

type PurchaseForm = {
  supplierId: string;
  locationId: string;
  expectedDate: string;
  notes: string;
  items: PurchaseFormItem[];
};

const createEmptyItem = (): PurchaseFormItem => ({
  productId: '',
  quantity: 1,
  unitCost: 0,
  taxRate: 0,
});

type MoneyValue = number | string;

interface ProductTaxLink {
  taxRate?: { rate?: MoneyValue | null; isActive?: boolean } | null;
}

/** Product row as returned by `GET /products` (only the fields this page reads). */
interface PurchaseProduct {
  id: string;
  name: string;
  sku?: string;
  costPrice?: MoneyValue | null;
  taxRate?: MoneyValue | null;
  taxOverride?: boolean;
  taxes?: ProductTaxLink[];
  category?: { taxes?: ProductTaxLink[] } | null;
}

interface PurchaseOrderItem {
  id: string;
  productName: string;
  productSku?: string | null;
  orderedQty: MoneyValue;
  receivedQty?: MoneyValue | null;
  unitCost?: MoneyValue | null;
  taxRate?: MoneyValue | null;
  taxAmount?: MoneyValue | null;
  totalAmount?: MoneyValue | null;
  product?: { serialTracking?: boolean } | null;
}

/** Purchase order row / detail as returned by `GET /purchases` and `GET /purchases/:id`. */
interface PurchaseOrder {
  id: string;
  orderNumber: string;
  poNumber?: string;
  status: string;
  supplier?: { name?: string } | null;
  subtotal?: MoneyValue | null;
  taxAmount?: MoneyValue | null;
  totalAmount: MoneyValue;
  createdAt: string;
  orderDate: string;
  locationId?: string | null;
  rejectionNote?: string | null;
  items?: PurchaseOrderItem[];
}

interface PurchasesResponse {
  data?: PurchaseOrder[];
  meta?: { total: number };
}

interface SupplierOption {
  id: string;
  name: string;
}

interface SuppliersResponse {
  data?: SupplierOption[];
  meta?: { total?: number };
}

interface ProductsResponse {
  data?: PurchaseProduct[];
}

interface PrefillItem {
  productId: string;
  productName: string;
  quantity: number;
}

interface CreatePurchasePayload {
  supplierId: string;
  items: { productId: string; quantity: number; unitCost: number; taxRate: number }[];
  locationId?: string;
  expectedDate?: string;
  notes?: string;
}

interface ReceiveItemPayload {
  purchaseItemId: string;
  receivedQty: number;
  serialNumbers?: string[];
}

function getProductDefaultTaxRate(product: PurchaseProduct | undefined) {
  const productTaxes = product?.taxOverride && product?.taxes?.length ? product.taxes : [];
  const categoryTaxes = product?.category?.taxes || [];
  const taxes = productTaxes.length > 0 ? productTaxes : categoryTaxes;
  const activeTaxes = taxes.filter((tax: ProductTaxLink) => tax.taxRate?.isActive !== false);

  if (activeTaxes.length > 0) {
    return activeTaxes.reduce((sum: number, tax: ProductTaxLink) => sum + Number(tax.taxRate?.rate || 0), 0);
  }

  return Number(product?.taxRate || 0);
}

function getItemSubtotal(item: PurchaseFormItem) {
  return item.quantity * item.unitCost;
}

function getItemTaxAmount(item: PurchaseFormItem) {
  return getItemSubtotal(item) * ((item.taxRate || 0) / 100);
}

function getItemTotal(item: PurchaseFormItem) {
  return getItemSubtotal(item) + getItemTaxAmount(item);
}

function printPurchaseOrder(po: PurchaseOrder) {
  const rows = (po.items || []).map((item: PurchaseOrderItem) => `
    <tr>
      <td>${escapeHtml(item.productName)}</td>
      <td>${escapeHtml(item.productSku || '')}</td>
      <td class="text-end">${Number(item.orderedQty || 0)}</td>
      <td class="text-end mono">${formatCurrency(Number(item.unitCost || 0))}</td>
      <td class="text-end">${Number(item.taxRate || 0)}%</td>
      <td class="text-end mono">${formatCurrency(Number(item.taxAmount || 0))}</td>
      <td class="text-end mono">${formatCurrency(Number(item.totalAmount || 0))}</td>
    </tr>
  `).join('');

  // POS-I240: shared printable-document builder (Powered by footer on every page).
  const opened = openPrintWindow({
    title: `Purchase ${po.orderNumber}`,
    extraCss: '.header{border-bottom:1px solid #ddd;padding-bottom:12px;margin-bottom:16px}h1{font-size:22px;margin:0}.totals{margin-left:auto;margin-top:16px;width:280px;font-size:13px}.totals div{display:flex;justify-content:space-between;padding:4px 0}.grand{border-top:1px solid #111;font-weight:700;margin-top:4px;padding-top:8px !important}',
    bodyHtml: `
        <div class="header">
          <h1>Purchase Invoice</h1>
          <div class="muted">PO: ${escapeHtml(po.orderNumber)} | ${escapeHtml(formatDate(po.createdAt || po.orderDate))}</div>
          <div class="muted">Supplier: ${escapeHtml(po.supplier?.name || 'Supplier')}</div>
          <div class="muted">Status: ${escapeHtml(po.status)}</div>
        </div>
        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th>SKU</th>
              <th class="text-end">Qty</th>
              <th class="text-end">Unit Cost</th>
              <th class="text-end">Tax %</th>
              <th class="text-end">Tax</th>
              <th class="text-end">Line Total</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="totals">
          <div><span>Subtotal</span><span class="mono">${formatCurrency(Number(po.subtotal || 0))}</span></div>
          <div><span>Tax</span><span class="mono">${formatCurrency(Number(po.taxAmount || 0))}</span></div>
          <div class="grand"><span>Total</span><span class="mono">${formatCurrency(Number(po.totalAmount || 0))}</span></div>
        </div>
        `,
  });
  if (!opened) toast.error('Unable to open print window');
}

function escapeHtml(value: unknown) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

export function PurchasesPage() {
  const { t } = useTranslation('purchases');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPurchaseId, setSelectedPurchaseId] = useState<string | null>(null);
  const [receiveDialogPo, setReceiveDialogPo] = useState<PurchaseOrder | null>(null);
  const [receiveLocationId, setReceiveLocationId] = useState<string>('');
  const [receiveSerials, setReceiveSerials] = useState<Record<string, string[]>>({});
  // POS-I254: per-line quantity being received now (string while typing); missing = the pending quantity.
  const [receiveQtys, setReceiveQtys] = useState<Record<string, string>>({});
  const [receiveDialogLoading, setReceiveDialogLoading] = useState(false);
  const [returnDialogPoId, setReturnDialogPoId] = useState<string | null>(null);
  const [returnReason, setReturnReason] = useState('');
  const [returnItems, setReturnItems] = useState<{ purchaseItemId: string; productName: string; maxQty: number; quantity: number }[]>([]);
  const [form, setForm] = useState<PurchaseForm>({
    supplierId: '', locationId: '', expectedDate: '', notes: '',
    items: [createEmptyItem()],
  });
  const [rejectDialogPoId, setRejectDialogPoId] = useState<string | null>(null);
  const [rejectionNote, setRejectionNote] = useState('');
  const [confirmApproveId, setConfirmApproveId] = useState<string | null>(null);
  const [barcodeInput, setBarcodeInput] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [debouncedProductSearch, setDebouncedProductSearch] = useState('');
  const barcodeRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const handlePurchaseUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['purchases'] });
    queryClient.invalidateQueries({ queryKey: ['inventory-products'] });
  }, [queryClient]);
  useSocketEvent('purchase.received', handlePurchaseUpdate);
  useSocketEvent('purchase.updated', handlePurchaseUpdate);
  const { hasPermission, user } = useAuthStore();
  const canCreate = hasPermission('purchases:create') || hasPermission('purchases:write') || hasPermission('purchases:*');
  const canReturn = hasPermission('purchases:write') || hasPermission('purchases:*');
  const canApprove = hasPermission('purchases:approve') || hasPermission('purchases:*');

  const [pendingPrefill, setPendingPrefill] = useState<{ productId: string; productName: string; quantity: number }[] | null>(null);

  // Phase 1: capture prefill from router state, open dialog immediately
  const routerLocation = useLocation();
  useEffect(() => {
    const state = routerLocation.state as { prefillPO?: { items: PrefillItem[] } } | null;
    if (state?.prefillPO) {
      const { items } = state.prefillPO;
      setPendingPrefill(items);
      setForm((prev) => ({
        ...prev,
        items: items.map((it) => ({ productId: it.productId, quantity: it.quantity || 1, unitCost: 0, taxRate: 0 })),
        notes: items.length === 1 ? `Reorder: ${items[0].productName}` : `Reorder: ${items.length} products`,
      }));
      setDialogOpen(true);
      window.history.replaceState({}, '');
    }
  }, [routerLocation.state]);

  const { data, isLoading } = useQuery<PurchasesResponse>({
    queryKey: ['purchases', search, page, statusFilter],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (search) params.set('search', search);
      if (statusFilter) params.set('status', statusFilter);
      const res = await api.get(`/purchases?${params}`);
      return res.data;
    },
  });

  const { data: suppliers } = useQuery<SuppliersResponse>({
    queryKey: ['suppliers'],
    queryFn: async () => {
      const res = await api.get('/suppliers?limit=100');
      return res.data;
    },
  });

  const { data: products } = useQuery<ProductsResponse>({
    queryKey: ['products-for-po', debouncedProductSearch],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '20' });
      if (debouncedProductSearch) params.set('search', debouncedProductSearch);
      const res = await api.get(`/products?${params}`);
      return res.data;
    },
    staleTime: 0,
  });

  // Phase 2: once products are loaded, back-fill cost & tax for pending prefill items
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedProductSearch(productSearch), 300);
    return () => clearTimeout(timer);
  }, [productSearch]);

  useEffect(() => {
    if (!pendingPrefill || !products?.data) return;
    setForm((prev) => ({
      ...prev,
      items: prev.items.map((item) => {
        const product = products.data?.find((p: PurchaseProduct) => p.id === item.productId);
        if (!product) return item;
        return {
          ...item,
          unitCost: Number(product.costPrice ?? 0),
          taxRate: getProductDefaultTaxRate(product),
        };
      }),
    }));
    setPendingPrefill(null);
  }, [pendingPrefill, products]);

  const { data: locationsForReceive } = useQuery({
    queryKey: ['locations-simple'],
    queryFn: async () => {
      const res = await api.get('/locations');
      const d = res.data;
      return (Array.isArray(d) ? d : d?.data ?? []) as { id: string; name: string; isDefault: boolean }[];
    },
    staleTime: 60_000,
  });

  const { data: selectedPurchase, isFetching: isPurchaseLoading } = useQuery<PurchaseOrder>({
    queryKey: ['purchase-detail', selectedPurchaseId],
    queryFn: async () => {
      const res = await api.get(`/purchases/${selectedPurchaseId}`);
      return res.data;
    },
    enabled: !!selectedPurchaseId,
  });

  const saveMutation = useMutation({
    mutationFn: async (payload: CreatePurchasePayload) => {
      const res = await api.post('/purchases', payload);
      return res.data;
    },
    onSuccess: (createdPurchase: PurchaseOrder) => {
      toast.success('Purchase order created');
      queryClient.invalidateQueries({ queryKey: ['purchases'] });
      setDialogOpen(false);
      setForm({ supplierId: '', locationId: '', expectedDate: '', notes: '', items: [createEmptyItem()] });
      setSelectedPurchaseId(createdPurchase.id);
    },
    onError: (err) => notify.error(err, 'Failed to create purchase order'),
  });

  const receiveMutation = useMutation({
    mutationFn: async ({ po, locationId }: { po: PurchaseOrder; locationId?: string }) => {
      // po is already the enriched detail (fetched before dialog opened), use directly
      const items = (po.items || [])
        .map((item: PurchaseOrderItem) => {
          const receivedQty = receiveQtyFor(item, receiveQtys);
          const payload: ReceiveItemPayload = { purchaseItemId: item.id, receivedQty };
          if (item.product?.serialTracking) {
            const entered = (receiveSerials[item.id] || []).filter((s: string) => s.trim() !== '');
            if (entered.length !== receivedQty) {
              throw new Error(
                `Enter all ${receivedQty} serial number(s) for "${item.productName}" before confirming.`,
              );
            }
            payload.serialNumbers = entered;
          }
          return payload;
        })
        .filter((item: ReceiveItemPayload) => item.receivedQty > 0);

      if (!items.length) {
        throw new Error('No pending items to receive');
      }

      return api.post(`/purchases/${po.id}/receive`, { items, locationId: locationId || undefined });
    },
    onSuccess: () => {
      toast.success('Purchase received and posted to accounting');
      queryClient.invalidateQueries({ queryKey: ['purchases'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-products'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['low-stock'] });
      queryClient.invalidateQueries({ queryKey: ['products-pos'] });
      queryClient.invalidateQueries({ queryKey: ['location-stock'] });
      setReceiveDialogPo(null);
      setReceiveLocationId('');
      setReceiveQtys({});
      setReceiveSerials({});
    },
    onError: (error: unknown) => {
      const apiError = isApiError(error) ? error : undefined;
      const message = apiError?.response?.data?.message || apiError?.message || 'Failed to receive purchase';
      toast.error(Array.isArray(message) ? message.join(', ') : message);
    },
  });

  const returnPoDetail = useQuery<PurchaseOrder>({
    queryKey: ['purchase-detail-return', returnDialogPoId],
    queryFn: () => api.get(`/purchases/${returnDialogPoId}`).then((r) => r.data),
    enabled: !!returnDialogPoId,
  });

  useEffect(() => {
    if (!returnPoDetail.data) return;
    const items = returnPoDetail.data.items ?? [];
    setReturnItems(items.map((item: PurchaseOrderItem) => ({
      purchaseItemId: item.id,
      productName: item.productName,
      maxQty: Number(item.receivedQty),
      quantity: Number(item.receivedQty),
    })));
  }, [returnPoDetail.data]);

  const returnMutation = useMutation({
    mutationFn: ({ id, reason, items }: { id: string; reason: string; items: { purchaseItemId: string; quantity: number }[] }) =>
      api.post(`/purchases/${id}/return`, { reason, items }),
    onSuccess: () => {
      toast.success('Purchase return posted');
      setReturnDialogPoId(null);
      setReturnReason('');
      setReturnItems([]);
      queryClient.invalidateQueries({ queryKey: ['purchases'] });
      queryClient.invalidateQueries({ queryKey: ['inventory-products'] });
      queryClient.invalidateQueries({ queryKey: ['tenant-dashboard'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['location-stock'] });
    },
    onError: (err: unknown) => {
      const message = isApiError(err) ? err.response?.data?.message : undefined;
      toast.error((Array.isArray(message) ? message.join(', ') : message) ?? 'Failed to process return');
    },
  });

  const submitForApprovalMutation = useMutation({
    mutationFn: (id: string) => api.post(`/purchases/${id}/submit-for-approval`),
    onSuccess: () => {
      toast.success('Purchase order submitted for approval');
      queryClient.invalidateQueries({ queryKey: ['purchases'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-detail', selectedPurchaseId] });
    },
    onError: (err) => notify.error(err, 'Failed to submit for approval'),
  });

  // POS-I239: Approved -> Ordered is its own step; Receive then works from Approved, Ordered and Partial.
  const orderMutation = useMutation({
    mutationFn: (id: string) => api.post(`/purchases/${id}/order`),
    onSuccess: () => {
      toast.success(t('markedAsOrderedToast'));
      queryClient.invalidateQueries({ queryKey: ['purchases'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-detail', selectedPurchaseId] });
    },
    onError: (err) => notify.error(err, t('markOrderedFailed')),
  });

  const openReceiveDialog = async (poId: string) => {
    setReceiveDialogLoading(true);
    try {
      const { data: detail } = await api.get(`/purchases/${poId}`);
      const defaultLoc = locationsForReceive?.find((l) => l.isDefault);
      setReceiveLocationId(detail.locationId ?? defaultLoc?.id ?? locationsForReceive?.[0]?.id ?? '');
      setReceiveQtys({});
      setReceiveSerials({});
      setReceiveDialogPo(detail);
    } catch {
      toast.error('Failed to load purchase order details');
    } finally {
      setReceiveDialogLoading(false);
    }
  };

  const approveMutation = useMutation({
    mutationFn: (id: string) => api.post(`/purchases/${id}/approve`),
    onSuccess: () => {
      toast.success('Purchase order approved');
      queryClient.invalidateQueries({ queryKey: ['purchases'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-detail', selectedPurchaseId] });
      setConfirmApproveId(null);
    },
    onError: (err) => notify.error(err, 'Failed to approve'),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, rejectionNote: note }: { id: string; rejectionNote: string }) =>
      api.post(`/purchases/${id}/reject`, { rejectionNote: note }),
    onSuccess: () => {
      toast.success('Purchase order rejected');
      queryClient.invalidateQueries({ queryKey: ['purchases'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-detail', selectedPurchaseId] });
      setRejectDialogPoId(null);
      setRejectionNote('');
    },
    onError: (err) => notify.error(err, 'Failed to reject'),
  });

  const handleReturnSubmit = () => {
    if (!returnDialogPoId) return;
    if (!returnReason.trim()) { toast.error('Return reason is required'); return; }
    const validItems = returnItems.filter((i) => i.quantity > 0)
      .map((i) => ({ purchaseItemId: i.purchaseItemId, quantity: i.quantity }));
    if (validItems.length === 0) { toast.error('Select at least one item to return'); return; }
    returnMutation.mutate({ id: returnDialogPoId, reason: returnReason, items: validItems });
  };

  const handleProductSelect = (index: number, productId: string) => {
    const product = products?.data?.find((p: PurchaseProduct) => p.id === productId);
    const newItems = [...form.items];
    newItems[index] = {
      ...newItems[index],
      productId,
      unitCost: product ? Number(product.costPrice) : 0,
      taxRate: product ? getProductDefaultTaxRate(product) : 0,
    };
    setForm({ ...form, items: newItems });
  };

  const handleItemChange = (index: number, field: 'quantity' | 'unitCost' | 'taxRate', value: number) => {
    const newItems = [...form.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setForm({ ...form, items: newItems });
  };

  const addItem = () => {
    setForm({ ...form, items: [...form.items, createEmptyItem()] });
  };

  const handleBarcodeScan = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || !barcodeInput.trim()) return;
    setIsScanning(true);
    try {
      const product = await posService.getProductByBarcode(barcodeInput.trim());
      if (product) {
        // Find existing empty row or add a new one
        const emptyIndex = form.items.findIndex((i) => !i.productId);
        const newItems = [...form.items];
        if (emptyIndex >= 0) {
          newItems[emptyIndex] = {
            ...newItems[emptyIndex],
            productId: product.id,
            unitCost: Number(product.costPrice ?? 0),
          };
        } else {
          newItems.push({
            productId: product.id,
            quantity: 1,
            unitCost: Number(product.costPrice ?? 0),
            taxRate: 0,
          });
        }
        setForm({ ...form, items: newItems });
        toast.success(`Added: ${product.name}`, { duration: 1500 });
        setBarcodeInput('');
      } else {
        toast.error('No product found for this barcode');
      }
    } finally {
      setIsScanning(false);
      barcodeRef.current?.focus();
    }
  };

  const removeItem = (index: number) => {
    if (form.items.length <= 1) return;
    setForm({ ...form, items: form.items.filter((_, i) => i !== index) });
  };

  const subtotal = form.items.reduce((sum, item) => sum + getItemSubtotal(item), 0);
  const totalTax = form.items.reduce((sum, item) => sum + getItemTaxAmount(item), 0);
  const total = subtotal + totalTax;

  const handleSave = () => {
    if (!form.supplierId) { toast.error('Please select a supplier'); return; }
    if (form.items.length === 0) {
      toast.error('Add at least one item');
      return;
    }
    const productIds = form.items.map((i) => i.productId);
    if (new Set(productIds).size !== productIds.length) {
      toast.error('Duplicate products found. Please combine quantities instead.');
      return;
    }
    if (form.items.some((item) => !item.productId || item.quantity <= 0 || item.unitCost < 0 || item.taxRate < 0)) {
      toast.error('Please fill in all item fields');
      return;
    }
    const payload: CreatePurchasePayload = {
      supplierId: form.supplierId,
      items: form.items.map((item) => ({
        productId: item.productId,
        quantity: Number(item.quantity),
        unitCost: Number(item.unitCost),
        taxRate: Number(item.taxRate || 0),
      })),
    };
    if (form.locationId) payload.locationId = form.locationId;
    if (form.expectedDate) payload.expectedDate = form.expectedDate;
    if (form.notes) payload.notes = form.notes;
    saveMutation.mutate(payload);
  };

  return (
    <div className="space-y-6 page-enter">
      <PageHeader
        title={t('purchasesTitle')}
        subtitle={t('subtitle')}
        action={
          <Button onClick={() => {
            const userLoc = user?.locationId ? locationsForReceive?.find((l) => l.id === user.locationId) : null;
            const defaultLoc = locationsForReceive?.find((l) => l.isDefault);
            setForm((prev) => ({ ...prev, locationId: userLoc?.id ?? defaultLoc?.id ?? locationsForReceive?.[0]?.id ?? '' }));
            setDialogOpen(true);
          }} variant="gradient" style={{ display: canCreate ? undefined : 'none' }}>
            <Plus className="me-2 h-4 w-4" />
            {t('newPurchaseOrder')}
          </Button>
        }
      />

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard
          className="animate-fade-in-up stagger-1"
          label={t('totalOrders')}
          value={data?.meta?.total || 0}
          format="number"
          icon={Truck}
          accent="info"
        />
        <StatCard
          className="animate-fade-in-up stagger-2"
          label={t('suppliersTitle')}
          value={suppliers?.meta?.total || suppliers?.data?.length || 0}
          format="number"
          icon={Package}
          accent="default"
        />
        <StatCard
          className="animate-fade-in-up stagger-3"
          label={t('pendingDelivery')}
          value={data?.data?.filter((p: PurchaseOrder) => p.status === 'ORDERED').length || 0}
          format="number"
          icon={Eye}
          accent="warning"
        />
      </div>

      {/* PO table */}
      <Card className="animate-fade-in-up stagger-4">
        <CardHeader>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className="text-lg">{t('purchaseOrders')}</CardTitle>
            <div className="flex items-center gap-2">
              <Dropdown
                value={statusFilter}
                onChange={e => { setStatusFilter(e.value); setPage(1); }}
                options={[
                  { label: t('allStatuses'), value: '' },
                  { label: t('draft'), value: 'DRAFT' },
                  { label: t('pendingApproval'), value: 'PENDING_APPROVAL' },
                  { label: t('approved'), value: 'APPROVED' },
                  { label: t('ordered'), value: 'ORDERED' },
                  { label: t('partial'), value: 'PARTIAL' },
                  { label: t('received'), value: 'RECEIVED' },
                  { label: t('cancelled'), value: 'CANCELLED' },
                ]}
                optionLabel="label"
                optionValue="value"
                placeholder={t('allStatuses')}
              />
              <SearchInput
                value={search}
                onChange={(value) => { setSearch(value); setPage(1); }}
                placeholder={t('searchPlaceholder')}
                className="w-64"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SkeletonTable rows={8} cols={6} className="mt-2" />
          ) : !data?.data?.length ? (
            <EmptyState
              icon={PackageSearch}
              title={t('noPurchaseOrdersTitle')}
              description={t('noPurchaseOrdersDesc')}
              action={canCreate ? { label: 'New Purchase Order', onClick: () => setDialogOpen(true) } : undefined}
            />
          ) : (
            <Table mobileCards>
                <THead>
                  <Tr hover={false}>
                    <Th>{t('poNumber')}</Th>
                    <Th>{t('supplier')}</Th>
                    <Th className="text-end">{t('total')}</Th>
                    <Th className="text-end">{t('tax')}</Th>
                    <Th className="text-center">{t('status')}</Th>
                    <Th>{t('date')}</Th>
                    <Th className="text-end">{t('actions')}</Th>
                  </Tr>
                </THead>
                <TBody>
                  {data.data.flatMap((po: PurchaseOrder) => {
                    const canReceive = po.status === 'APPROVED' || po.status === 'ORDERED' || po.status === 'PARTIAL';
                    const canReceiveAction = canReceive && (hasPermission('purchases:write') || hasPermission('purchases:*'));
                    const orderedQty = (po.items || []).reduce((s: number, i: PurchaseOrderItem) => s + Number(i.orderedQty || 0), 0);
                    const receivedQty = (po.items || []).reduce((s: number, i: PurchaseOrderItem) => s + Number(i.receivedQty || 0), 0);
                    const showProgress = orderedQty > 0 && (po.status === 'ORDERED' || po.status === 'PARTIAL' || po.status === 'RECEIVED');
                    const pct = orderedQty > 0 ? (receivedQty / orderedQty) * 100 : 0;

                    const mainRow = (
                      <Tr key={po.id}>
                        <Td label={t('poNumber')} className="font-mono text-sm font-medium td-name">{po.orderNumber}</Td>
                        <Td label={t('supplier')}>{po.supplier?.name || '—'}</Td>
                        <Td label={t('total')} className="text-end font-medium">{formatCurrency(Number(po.totalAmount))}</Td>
                        <Td label={t('tax')} className="text-end font-mono text-xs td-hide">{formatCurrency(Number(po.taxAmount || 0))}</Td>
                        <Td label={t('status')} className="text-center">
                          <StatusBadge status={po.status} />
                        </Td>
                        <Td label={t('date')} className="text-muted-foreground td-hide">
                          {formatDate(po.createdAt)}
                        </Td>
                        <Td label={t('actions')} className="text-end td-actions">
                          <TableRowActions
                            actions={[
                              {
                                key: 'view',
                                label: t('view'),
                                icon: Eye,
                                onSelect: () => setSelectedPurchaseId(po.id),
                                primary: true,
                                labeled: true,
                              },
                              {
                                key: 'print',
                                label: t('print'),
                                icon: Printer,
                                onSelect: async () => {
                                  try {
                                    const res = await api.get(`/purchases/${po.id}`);
                                    printPurchaseOrder(res.data);
                                  } catch {
                                    toast.error('Could not load purchase details for printing');
                                  }
                                },
                                primary: true,
                                labeled: true,
                              },
                              {
                                key: 'order',
                                label: t('markAsOrdered'),
                                icon: Package,
                                onSelect: () => orderMutation.mutate(po.id),
                                permission: canCreate && po.status === 'APPROVED',
                                labeled: true,
                                disabled: orderMutation.isPending,
                              },
                              {
                                key: 'receive',
                                label: t('receiveAndPost'),
                                icon: CheckCircle2,
                                onSelect: () => openReceiveDialog(po.id),
                                permission: canReceiveAction,
                                labeled: true,
                                disabled: receiveMutation.isPending || receiveDialogLoading,
                              },
                              {
                                key: 'return',
                                label: t('returnPurchase'),
                                icon: Package,
                                onSelect: () => { setReturnDialogPoId(po.id); setReturnReason(''); setReturnItems([]); },
                                permission: canReturn && po.status === 'RECEIVED',
                                labeled: true,
                              },
                              {
                                key: 'submit',
                                label: t('submitForApproval'),
                                onSelect: () => submitForApprovalMutation.mutate(po.id),
                                permission: canCreate && po.status === 'DRAFT',
                                labeled: true,
                                disabled: submitForApprovalMutation.isPending,
                              },
                              {
                                key: 'approve',
                                label: t('approvePurchase'),
                                icon: CheckCircle2,
                                onSelect: () => setConfirmApproveId(po.id),
                                permission: canApprove && po.status === 'PENDING_APPROVAL',
                                labeled: true,
                                disabled: approveMutation.isPending,
                              },
                              {
                                key: 'reject',
                                label: t('rejectPurchase'),
                                onSelect: () => setRejectDialogPoId(po.id),
                                permission: canApprove && po.status === 'PENDING_APPROVAL',
                                variant: 'destructive',
                                labeled: true,
                                disabled: rejectMutation.isPending,
                              },
                            ]}
                          />
                        </Td>
                      </Tr>
                    );

                    return [
                      mainRow,
                      ...(po.rejectionNote && po.status === 'DRAFT' ? [
                        <Tr key={`${po.id}-rejection`} hover={false} className="no-card">
                          <Td colSpan={7} className="pb-2 pt-0">
                            <p className="flex items-center gap-1 text-xs text-destructive-text">
                              ⚠ Rejection note: {po.rejectionNote}
                            </p>
                          </Td>
                        </Tr>,
                      ] : []),
                      ...(showProgress ? [
                        <Tr key={`${po.id}-progress`} hover={false} className="no-card">
                          <Td colSpan={7} className="pb-2.5 pt-0">
                            <div className="flex justify-between text-xs text-muted-foreground mb-1">
                              <span>{receivedQty} / {orderedQty} items received</span>
                              <span>{Math.round(pct)}%</span>
                            </div>
                            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                              <div style={{ width: `${Math.min(100, pct)}%` }} className="h-1.5 bg-success-solid rounded-full transition-all duration-300" />
                            </div>
                          </Td>
                        </Tr>,
                      ] : []),
                    ];
                  })}
                </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {data?.meta && (
        <PaginationBar
          page={page}
          pageSize={20}
          total={data.meta.total}
          onPageChange={setPage}
        />
      )}

      {/* New Purchase Order Dialog */}
      <Dialog
        visible={dialogOpen}
        onHide={() => setDialogOpen(false)}
        header={t('createPurchaseOrder')}
        footer={
          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>{t('cancel')}</Button>
            <Button
              type="submit"
              form="purchase-form"
              disabled={saveMutation.isPending}
              variant="gradient"
              className="flex-1"
            >
              {saveMutation.isPending ? t('creating') : t('createPurchaseOrder')}
            </Button>
          </div>
        }
        style={{ width: '56rem' }}
        draggable={false}
        resizable={false}
      >
        <form id="purchase-form" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
        <div className="space-y-4">
          <div>
            <Label className="mb-1.5 block">{t('supplier')} <span className="text-destructive-text">*</span></Label>
            <Combobox
              options={(suppliers?.data || []).map((s: SupplierOption) => ({ value: s.id, label: s.name }))}
              value={form.supplierId}
              onChange={(val) => setForm({ ...form, supplierId: val })}
              placeholder="Select supplier..."
              searchPlaceholder="Search suppliers..."
              emptyText="No suppliers found."
            />
          </div>

          {/* Receiving Location */}
          {locationsForReceive && locationsForReceive.length > 0 && (
            <div>
              <Label className="mb-1.5 block">
                {t('receivingLocation')}
                {(locationsForReceive.length === 1 || !!user?.locationId) && (
                  <span className="ms-2 text-xs font-normal text-muted-foreground">{t('autoSelected')}</span>
                )}
              </Label>
              <Dropdown
                value={form.locationId}
                onChange={(e) => setForm({ ...form, locationId: e.value })}
                disabled={locationsForReceive.length === 1 || !!user?.locationId}
                options={[{ label: t('noSpecificLocation'), value: '' }, ...locationsForReceive.map((loc) => ({ label: `${loc.name}${loc.isDefault ? ' (Default)' : ''}`, value: loc.id }))]}
                optionLabel="label"
                optionValue="value"
                placeholder={t('noSpecificLocation')}
                className="w-full"
              />
              <p className="text-xs text-muted-foreground mt-1">Stock will be added to this location when goods are received.</p>
            </div>
          )}

          <div>
            <Label className="mb-1.5 block">{t('expectedDate')}</Label>
            <Input
              type="date"
              value={form.expectedDate}
              onChange={(e) => setForm({ ...form, expectedDate: e.target.value })}
            />
          </div>

          <div>
            <Label className="mb-1.5 block">{t('notes')}</Label>
            <Textarea
              placeholder="Optional notes..."
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
            />
          </div>

          {/* Items Section */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>{t('items')} <span className="text-destructive-text">*</span></Label>
              <Button type="button" variant="outline" size="sm" onClick={addItem}>
                <Plus className="me-1 h-3 w-3" />
                {t('addItem')}
              </Button>
            </div>

            {/* Barcode scan input */}
            <div className="mb-2 flex items-center gap-2 rounded-lg border bg-accent/30 px-3 py-2">
              {isScanning ? (
                <ScanBarcode className="h-4 w-4 text-primary-text animate-pulse shrink-0" />
              ) : (
                <ScanBarcode className="h-4 w-4 text-muted-foreground shrink-0" />
              )}
              <Input
                ref={barcodeRef}
                value={barcodeInput}
                onChange={(e) => setBarcodeInput(e.target.value)}
                onKeyDown={handleBarcodeScan}
                placeholder={t('scanBarcodePlaceholder')}
                className="border-0 bg-transparent p-0 h-auto focus-visible:ring-0 text-sm"
              />
            </div>
            <div className="overflow-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50 text-start text-muted-foreground">
                    <th className="px-3 py-2 font-medium">{t('product')}</th>
                    <th className="px-3 py-2 font-medium w-24">{t('quantity')}</th>
                    <th className="px-3 py-2 font-medium w-32">{t('unitCost')}</th>
                    <th className="px-3 py-2 font-medium w-24">{t('taxPercent')}</th>
                    <th className="px-3 py-2 font-medium w-28 text-end">{t('tax')}</th>
                    <th className="px-3 py-2 font-medium w-32 text-end">{t('lineTotal')}</th>
                    <th className="px-3 py-2 w-10"></th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {form.items.map((item, index) => (
                    <tr key={index}>
                      <td className="px-3 py-2">
                        <Combobox
                          options={(products?.data || []).map((p: PurchaseProduct) => ({ value: p.id, label: p.name, sublabel: p.sku }))}
                          value={item.productId}
                          onChange={(val) => handleProductSelect(index, val)}
                          placeholder="Select product..."
                          searchPlaceholder="Search products..."
                          emptyText="No products found."
                          onFilterChange={setProductSearch}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          type="number"
                          min="1"
                          className="w-full"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', Number(e.target.value))}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          className="w-full"
                          value={item.unitCost}
                          onChange={(e) => handleItemChange(index, 'unitCost', Number(e.target.value))}
                        />
                      </td>
                      <td className="px-3 py-2">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          className="w-full"
                          value={item.taxRate}
                          onChange={(e) => handleItemChange(index, 'taxRate', Number(e.target.value))}
                        />
                      </td>
                      <td className="px-3 py-2 text-end font-medium">
                        {formatCurrency(getItemTaxAmount(item))}
                      </td>
                      <td className="px-3 py-2 text-end font-medium">
                        {formatCurrency(getItemTotal(item))}
                      </td>
                      <td className="px-3 py-2">
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          disabled={form.items.length <= 1}
                          className="rounded-lg p-1 hover:bg-destructive/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                        >
                          <Trash2 className="h-4 w-4 text-destructive-text" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end mt-3">
              <div className="w-64 space-y-1 text-sm">
                <div className="flex justify-between text-muted-foreground">
                  <span>{t('subtotal')}</span>
                  <span>{formatCurrency(subtotal)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>{t('tax')}</span>
                  <span>{formatCurrency(totalTax)}</span>
                </div>
                <div className="flex justify-between font-semibold">
                  <span>{t('total')}</span>
                  <span>{formatCurrency(total)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        </form>
      </Dialog>

      {/* Purchase Detail Dialog */}
      <Dialog
        visible={!!selectedPurchaseId}
        onHide={() => setSelectedPurchaseId(null)}
        header={
          <div>
            <p className="font-semibold">{t('purchaseInvoice')}</p>
            <p className="text-sm text-muted-foreground font-normal">
              {selectedPurchase?.orderNumber || 'Loading...'}
            </p>
          </div>
        }
        footer={
          selectedPurchase ? (
            <div className="flex flex-wrap justify-end gap-2" data-testid="po-workflow-actions">
              {/* POS-I239: the whole Draft -> Approved -> Ordered -> Received path from the order itself */}
              {canCreate && selectedPurchase.status === 'DRAFT' && (
                <Button type="button" size="sm" onClick={() => submitForApprovalMutation.mutate(selectedPurchase.id)} disabled={submitForApprovalMutation.isPending}>
                  {t('submitForApproval')}
                </Button>
              )}
              {canApprove && selectedPurchase.status === 'PENDING_APPROVAL' && (
                <Button type="button" size="sm" onClick={() => setConfirmApproveId(selectedPurchase.id)} disabled={approveMutation.isPending}>
                  <CheckCircle2 className="me-1 h-3.5 w-3.5" />
                  {t('approvePurchase')}
                </Button>
              )}
              {canCreate && selectedPurchase.status === 'APPROVED' && (
                <Button type="button" size="sm" onClick={() => orderMutation.mutate(selectedPurchase.id)} disabled={orderMutation.isPending}>
                  <Package className="me-1 h-3.5 w-3.5" />
                  {t('markAsOrdered')}
                </Button>
              )}
              {canCreate && ['APPROVED', 'ORDERED', 'PARTIAL'].includes(selectedPurchase.status) && (
                <Button type="button" size="sm" onClick={() => { const id = selectedPurchase.id; setSelectedPurchaseId(null); void openReceiveDialog(id); }} disabled={receiveDialogLoading}>
                  <CheckCircle2 className="me-1 h-3.5 w-3.5" />
                  {t('receiveAndPost')}
                </Button>
              )}
              <Button type="button" variant="outline" size="sm" onClick={() => printPurchaseOrder(selectedPurchase)}>
                <Printer className="me-1 h-3.5 w-3.5" />
                {t('print')}
              </Button>
            </div>
          ) : null
        }
        style={{ width: '56rem' }}
        draggable={false}
        resizable={false}
      >
        {isPurchaseLoading || !selectedPurchase ? (
          <div className="flex items-center justify-center py-12 text-muted-foreground">
            {t('loadingPurchaseInvoice')}
          </div>
        ) : (
          <div className="space-y-5">
            <div className="grid gap-3 rounded-lg border bg-muted/30 p-4 text-sm sm:grid-cols-2">
              <div>
                <p className="text-muted-foreground">Supplier</p>
                <p className="font-medium">{selectedPurchase.supplier?.name || 'Supplier'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Date</p>
                <p className="font-medium">{formatDate(selectedPurchase.createdAt || selectedPurchase.orderDate)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Status</p>
                <p className="font-medium">{selectedPurchase.status}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Tax</p>
                <p className="font-medium">{formatCurrency(Number(selectedPurchase.taxAmount || 0))}</p>
              </div>
            </div>

            <div className="overflow-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50 text-start text-muted-foreground">
                    <th className="px-3 py-2 font-medium">{t('product')}</th>
                    <th className="px-3 py-2 font-medium">SKU</th>
                    <th className="px-3 py-2 font-medium text-end">{t('quantity')}</th>
                    <th className="px-3 py-2 font-medium text-end">{t('unitCost')}</th>
                    <th className="px-3 py-2 font-medium text-end">{t('taxPercent')}</th>
                    <th className="px-3 py-2 font-medium text-end">{t('tax')}</th>
                    <th className="px-3 py-2 font-medium text-end">{t('lineTotal')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(selectedPurchase.items || []).map((item: PurchaseOrderItem) => (
                    <tr key={item.id}>
                      <td className="px-3 py-2 font-medium">{item.productName}</td>
                      <td className="px-3 py-2 text-muted-foreground">{item.productSku || '-'}</td>
                      <td className="px-3 py-2 text-end">{item.orderedQty}</td>
                      <td className="px-3 py-2 text-end font-mono text-xs">{formatCurrency(Number(item.unitCost || 0))}</td>
                      <td className="px-3 py-2 text-end">{Number(item.taxRate || 0)}%</td>
                      <td className="px-3 py-2 text-end font-mono text-xs">{formatCurrency(Number(item.taxAmount || 0))}</td>
                      <td className="px-3 py-2 text-end font-mono text-xs font-semibold">{formatCurrency(Number(item.totalAmount || 0))}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end">
              <div className="w-72 space-y-1 text-sm">
                <div className="flex justify-between text-muted-foreground">
                  <span>{t('subtotal')}</span>
                  <span>{formatCurrency(Number(selectedPurchase.subtotal || 0))}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>{t('tax')}</span>
                  <span>{formatCurrency(Number(selectedPurchase.taxAmount || 0))}</span>
                </div>
                <div className="mt-2 flex justify-between border-t pt-2 text-base font-semibold">
                  <span>{t('total')}</span>
                  <span>{formatCurrency(Number(selectedPurchase.totalAmount || 0))}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </Dialog>

      {/* Receive Purchase Order dialog — location selector */}
      <Dialog
        visible={!!receiveDialogPo}
        onHide={() => { setReceiveDialogPo(null); setReceiveLocationId(''); setReceiveSerials({}); setReceiveQtys({}); }}
        modal
        header={t('receivePurchaseOrderTitle')}
        style={{ width: '28rem', maxWidth: '90vw' }}
        pt={{ root: { className: 'rounded-2xl border shadow-2xl' } }}
        footer={
          <div className="flex gap-3">
            <Button variant="outline" className="flex-1" onClick={() => { setReceiveDialogPo(null); setReceiveLocationId(''); setReceiveSerials({}); setReceiveQtys({}); }}>{t('cancel')}</Button>
            <Button
              className="flex-1"
              disabled={receiveMutation.isPending || !(receiveDialogPo?.items ?? []).some((item: PurchaseOrderItem) => receiveQtyFor(item, receiveQtys) > 0) || (() => {
                // Disable if any serial-tracked item has incomplete serial entry
                if (!receiveDialogPo?.items) return false;
                return receiveDialogPo.items.some((item: PurchaseOrderItem) => {
                  if (!item.product?.serialTracking) return false;
                  const pendingQty = receiveQtyFor(item, receiveQtys);
                  if (pendingQty <= 0) return false;
                  const entered = (receiveSerials[item.id] || []).filter((s: string) => s.trim() !== '');
                  return entered.length !== pendingQty;
                });
              })()}
              onClick={() => { if (receiveDialogPo) receiveMutation.mutate({ po: receiveDialogPo, locationId: receiveLocationId }); }}
            >
              {receiveMutation.isPending && <span className="me-2 h-4 w-4 animate-spin inline-block border-2 border-t-transparent rounded-full" />}
              {t('confirmReceive')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 py-2">
          <p className="text-sm text-muted-foreground">
            Posting stock for: <span className="font-medium text-foreground">{receiveHeading(receiveDialogPo)}</span>
          </p>
          {receiveDialogPo?.items && receiveDialogPo.items.length > 0 && (
            <div className="space-y-2" data-testid="receive-lines">
              <Label className="block">Quantity to receive now</Label>
              {receiveDialogPo.items.map((item: PurchaseOrderItem) => {
                const pending = linePendingQty(item);
                return (
                  <div key={item.id} className="flex items-center justify-between gap-3 rounded-lg border p-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium">{item.productName}</p>
                      <p className="text-xs text-muted-foreground">
                        Ordered {Number(item.orderedQty)} · Received {Number(item.receivedQty || 0)} · Pending {pending}
                      </p>
                    </div>
                    <Input
                      type="number"
                      min={0}
                      max={pending}
                      step={1}
                      disabled={pending <= 0}
                      aria-label={`Quantity to receive for ${item.productName}`}
                      value={receiveQtys[item.id] ?? String(pending)}
                      onChange={(e) => setReceiveQtys({ ...receiveQtys, [item.id]: e.target.value })}
                      className="w-24 text-end"
                    />
                  </div>
                );
              })}
            </div>
          )}
          <div>
            <Label className="mb-1.5 block">
              {t('receivingLocation')}
              {(locationsForReceive && locationsForReceive.length === 1 || !!user?.locationId) && (
                <span className="ms-2 text-xs font-normal text-muted-foreground">{t('autoSelected')}</span>
              )}
            </Label>
            <Dropdown
              value={receiveLocationId}
              onChange={(e) => setReceiveLocationId(e.value)}
              disabled={!!(locationsForReceive && locationsForReceive.length === 1) || !!user?.locationId}
              options={[{ label: t('noSpecificLocation'), value: '' }, ...(locationsForReceive ?? []).map((loc) => ({ label: `${loc.name}${loc.isDefault ? ' (Default)' : ''}`, value: loc.id }))]}
              optionLabel="label"
              optionValue="value"
              placeholder={t('noSpecificLocation')}
              className="w-full"
            />
            {!receiveLocationId && (
              <p className="mt-1 text-xs text-muted-foreground">If no location is selected, only global product stock will be updated.</p>
            )}
          </div>
        </div>

        {/* Serial Number Input Section */}
        {receiveDialogPo?.items && receiveDialogPo.items.some((item: PurchaseOrderItem) => item.product?.serialTracking) && (
          <div className="border-t pt-4">
            <p className="text-sm font-medium mb-3">Serial Numbers / IMEI Codes</p>
            <div className="space-y-3">
              {receiveDialogPo.items.map((item: PurchaseOrderItem) => {
                if (!item.product?.serialTracking) return null;
                const pendingQty = receiveQtyFor(item, receiveQtys);
                if (pendingQty <= 0) return null;

                const entered = (receiveSerials[item.id] || []).filter((s: string) => s.trim() !== '');
                const isComplete = entered.length === pendingQty;

                return (
                  <div key={item.id} className={`rounded-lg border p-3 ${isComplete ? 'border-success/30 bg-success/10' : 'border-info/30 bg-info/10'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium">{item.productName}</p>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isComplete ? 'bg-success/10 text-success-text' : 'bg-warning/10 text-warning-text'}`}>
                        {entered.length} / {pendingQty} entered
                      </span>
                    </div>
                    <div className="space-y-2">
                      {Array.from({ length: pendingQty }).map((_, i) => (
                        <Input
                          key={`${item.id}-${i}`}
                          type="text"
                          placeholder={`Serial #${i + 1} (e.g., SN-001, VIN, IMEI)`}
                          value={receiveSerials[item.id]?.[i] || ''}
                          onChange={(e) => {
                            const newSerials = [...(receiveSerials[item.id] || [])];
                            newSerials[i] = e.target.value;
                            setReceiveSerials({ ...receiveSerials, [item.id]: newSerials });
                          }}
                          className="text-xs"
                        />
                      ))}
                    </div>
                    {!isComplete && (
                      <p className="mt-2 text-xs text-warning-text">
                        ⚠ Enter all {pendingQty} serial numbers before confirming receive.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Dialog>
      {/* Return Purchase Order Dialog */}
      <Dialog
        visible={!!returnDialogPoId}
        onHide={() => { setReturnDialogPoId(null); setReturnReason(''); setReturnItems([]); }}
        header={
          <div className="flex items-center gap-2">
            <Package className="h-4 w-4 text-warning-text" />
            <span>{t('returnToSupplier')}</span>
          </div>
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setReturnDialogPoId(null)}>{t('cancel')}</Button>
            <Button
              onClick={handleReturnSubmit}
              disabled={returnMutation.isPending}
              className="bg-warning hover:bg-warning/90 text-warning-foreground"
            >
              {returnMutation.isPending && <span className="me-1.5 h-3.5 w-3.5 animate-spin inline-block border-2 border-t-transparent rounded-full" />}
              {t('confirmReturn')}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 py-1">
          <div>
            <Label className="mb-1.5 block">{t('reason')} <span className="text-destructive-text">*</span></Label>
            <Input
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              placeholder="Enter return reason…"
              className="h-9"
            />
          </div>

          {returnPoDetail.isFetching ? (
            <div className="flex items-center justify-center py-6 gap-2 text-muted-foreground text-sm">
              <span className="h-4 w-4 animate-spin inline-block border-2 border-t-transparent rounded-full" />
              Loading items…
            </div>
          ) : returnItems.length > 0 ? (
            <div className="rounded-lg border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30 text-xs text-muted-foreground">
                    <th className="px-3 py-2 text-start font-medium">{t('product')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('received')}</th>
                    <th className="px-3 py-2 text-end font-medium">{t('returnQty')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {returnItems.map((item, idx) => (
                    <tr key={item.purchaseItemId}>
                      <td className="px-3 py-2">{item.productName}</td>
                      <td className="px-3 py-2 text-end text-muted-foreground">{item.maxQty}</td>
                      <td className="px-3 py-2 text-end">
                        <Input
                          type="number"
                          min={0}
                          max={item.maxQty}
                          step={1}
                          value={item.quantity}
                          onChange={(e) => {
                            const v = Math.min(item.maxQty, Math.max(0, Number(e.target.value)));
                            setReturnItems((prev) => prev.map((ri, i) => i === idx ? { ...ri, quantity: v } : ri));
                          }}
                          className="w-20 h-7 px-2 py-0 text-end"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </Dialog>

      {/* Reject Purchase Order Dialog */}
      <FormDialog
        open={!!rejectDialogPoId}
        title={t('rejectPurchaseOrderTitle')}
        width="28rem"
        submitLabel={t('rejectPurchase')}
        variant="destructive"
        isPending={rejectMutation.isPending}
        submitDisabled={!rejectionNote.trim()}
        onClose={() => { setRejectDialogPoId(null); setRejectionNote(''); }}
        onSubmit={(e) => {
          e.preventDefault();
          if (!rejectDialogPoId || !rejectionNote.trim()) return;
          rejectMutation.mutate({ id: rejectDialogPoId, rejectionNote });
        }}
      >
        <p className="text-sm text-muted-foreground">{t('provideRejectionReason')}</p>
        <Textarea
          value={rejectionNote}
          onChange={(e) => setRejectionNote(e.target.value)}
          rows={3}
          placeholder={t('rejectionReason')}
        />
      </FormDialog>

      {/* Approve Purchase Order Confirmation */}
      <ConfirmDialog
        open={!!confirmApproveId}
        title={t('approvePurchaseOrderTitle')}
        message={t('approveConfirmMessage')}
        confirmLabel={t('approvePurchase')}
        variant="warning"
        loading={approveMutation.isPending}
        onConfirm={() => { if (confirmApproveId) approveMutation.mutate(confirmApproveId); }}
        onCancel={() => setConfirmApproveId(null)}
      />
    </div>
  );
}

